import { keepPreviousData, useQuery } from "@tanstack/react-query";
import axios from "../../services/axios/config";
import { getTestTableColumns } from "../../services/rules";

// Shared TanStack hooks for the rule/columns + rule/columns/data
// endpoints. Used by both TestResultsTable (RuleEditor) and
// FilterResultsTable (AdvancedSearch).
//
// The two pages differ in WHO triggers the fetch and which slice
// holds the page/take state — both pass an `enabled` flag in. The
// queries themselves are identical.

// Cheap call (just the column names), no pagination.
//
// `nonce` is the Test/Apply click counter. Including it in the query key
// means every click is a distinct query → a guaranteed fresh request,
// instead of TanStack serving the cached result for an unchanged ruleId
// (which it did before, so edits to the rule between clicks were ignored).
export function useRuleColumnsQuery({ ruleId, nonce = 0, enabled = true }) {
  return useQuery({
    queryKey: ["rule-columns", ruleId, nonce],
    queryFn: () => getTestTableColumns(ruleId),
    enabled: Boolean(ruleId && enabled),
    refetchOnWindowFocus: false,
    retry: 0,
    // Keep the prior headers on screen while the new nonce's columns load,
    // so re-running Test doesn't blank the table header for a frame.
    placeholderData: keepPreviousData,
  });
}

// Returns { rows, totalCount }; rows are extracted from the server's
// `columnData[].data` shape.
async function fetchRuleColumnsData({ ruleId, page, take }) {
  const res = await axios.get(
    `rule/columns/data/${ruleId}?page=${page}&size=${take}`,
  );
  const data = res.data;
  return {
    rows: (data?.columnData ?? []).map((row) => row.data),
    totalCount: data?.totalCount ?? 0,
  };
}

export function useRuleColumnsDataQuery({
  ruleId,
  page,
  take,
  nonce = 0,
  enabled = true,
}) {
  return useQuery({
    queryKey: ["rule-columns-data", ruleId, nonce, page, take],
    queryFn: () => fetchRuleColumnsData({ ruleId, page, take }),
    enabled: Boolean(ruleId && enabled),
    refetchOnWindowFocus: false,
    retry: 0,
    // Keep the current page's rows on screen while the next page loads,
    // so changing pages doesn't flash the skeleton (which causes the
    // table to jump). The skeleton then only shows on the very first load.
    placeholderData: keepPreviousData,
  });
}
